function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); return Constructor; }

/**
 * @license Copyright (c) 2003-2021, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

/* global setTimeout, clearTimeout */

/**
 * @module utils/focustracker
 */
import DomEmitterMixin from './dom/emittermixin';
import ObservableMixin from './observablemixin';
import CKEditorError from './ckeditorerror';
import mix from './mix';
/**
 * Allows observing a group of `HTMLElement`s whether at least one of them is focused.
 *
 * Used by the {@link module:core/editor/editor~Editor} in order to track whether the focus is still within the application,
 * or were used outside of its UI.
 *
 * **Note** `focus` and `blur` listeners use event capturing, so it is only needed to register wrapper `HTMLElement`
 * which contain other `focusable` elements. But note that this wrapper element has to be focusable too
 * (have e.g. `tabindex="-1"`).
 *
 * Check out the {@glink framework/guides/deep-dive/ui/focus-tracking "Deep dive into focus tracking" guide} to learn more.
 *
 * @mixes module:utils/dom/emittermixin~EmitterMixin
 * @mixes module:utils/observablemixin~ObservableMixin
 */

var FocusTracker = /*#__PURE__*/function () {
  function FocusTracker() {
    _classCallCheck(this, FocusTracker);

    /**
     * True when one of the registered elements is focused.
     *
     * @readonly
     * @observable
     * @member {Boolean} #isFocused
     */
    this.set('isFocused', false);
    /**
     * The currently focused element.
     *
     * While {@link #isFocused `isFocused`} remains `true`, the focus can
     * move between different UI elements. This property tracks those
     * elements and tells which one is currently focused.
     *
     * @readonly
     * @observable
     * @member {HTMLElement|null} #focusedElement
     */

    this.set('focusedElement', null);
    /**
     * List of registered elements.
     *
     * @private
     * @member {Set.<HTMLElement>}
     */

    this._elements = new Set();
    /**
     * Event loop timeout.
     *
     * @private
     * @member {Number}
     */

    this._nextEventLoopTimeout = null;
  }
  /**
   * Starts tracking the specified element.
   *
   * @param {HTMLElement} element
   */


  _createClass(FocusTracker, [{
    key: "add",
    value: function add(element) {
      var _this = this;

      if (this._elements.has(element)) {
        /**
         * This element is already tracked by {@link module:utils/focustracker~FocusTracker}.
         *
         * @error focustracker-add-element-already-exist
         */
        throw new CKEditorError('focustracker-add-element-already-exist', this);
      }

      this.listenTo(element, 'focus', function () {
        return _this._focus(element);
      }, {
        useCapture: true
      });
      this.listenTo(element, 'blur', function () {
        return _this._blur();
      }, {
        useCapture: true
      });

      this._elements.add(element);
    }
    /**
     * Stops tracking the specified element and stops listening on this element.
     *
     * @param {HTMLElement} element
     */

  }, {
    key: "remove",
    value: function remove(element) {
      if (element === this.focusedElement) {
        this._blur(element);
      }

      if (this._elements.has(element)) {
        this.stopListening(element);

        this._elements.delete(element);
      }
    }
    /**
     * Destroys the focus tracker by:
     * - Disabling all event listeners attached to tracked elements.
     * - Removing all tracked elements that were previously added.
     */

  }, {
    key: "destroy",
    value: function destroy() {
      this.stopListening();
    }
    /**
     * Stores currently focused element and set {#isFocused} as `true`.
     *
     * @private
     * @param {HTMLElement} element Element which has been focused.
     */

  }, {
    key: "_focus",
    value: function _focus(element) {
      clearTimeout(this._nextEventLoopTimeout);
      this.focusedElement = element;
      this.isFocused = true;
    }
    /**
     * Clears currently focused element and set {@link #isFocused} as `false`.
     * This method uses `setTimeout` to change order of fires `blur` and `focus` events.
     *
     * @private
     * @fires blur
     */

  }, {
    key: "_blur",
    value: function _blur() {
      var _this2 = this;

      clearTimeout(this._nextEventLoopTimeout);
      this._nextEventLoopTimeout = setTimeout(function () {
        _this2.focusedElement = null;
        _this2.isFocused = false;
      }, 0);
    }
    /**
     * @event focus
     */

    /**
     * @event blur
     */

  }]);

  return FocusTracker;
}();

export { FocusTracker as default };
mix(FocusTracker, DomEmitterMixin);
mix(FocusTracker, ObservableMixin);